import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Play, X, Loader2 } from 'lucide-react';
import { loadTutorialsManifest } from '../../../utils/tutorialsManifest';
import { trackTutorialEvent, getVisitorEmail } from '../../../utils/tutorialAnalytics';
import { usePageTracking } from '../../../hooks/usePageTracking';

const TutorialsView = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [listState, setListState] = useState('loading');
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const videoRef = useRef(null);
  const pageViewOnce = useRef(false);

  usePageTracking('tutorials', { email: getVisitorEmail() || undefined });

  useEffect(() => {
    if (pageViewOnce.current) return;
    pageViewOnce.current = true;
    trackTutorialEvent({
      event: 'page_view',
      source: 'dashboard_manifest',
      tutorialId: 'page_dashboard_tutorials',
      path: typeof window !== 'undefined' ? window.location.pathname : ''
    });
  }, []);

  const handleLoadList = async () => {
    try {
      setListState('loading');
      setError('');
      setSelected(null);
      setItems([]);
      const list = await loadTutorialsManifest();
      setItems(list);
      if (list.length === 0) {
        setError(
          t(
            'tutorials.emptyListHint',
            'Proverite da li je na serveru podešen R2 (Cloudflare) i da u bucketu postoji tuts/tutorials.json i video fajlovi. Na Netlify-u po potrebi postavite REACT_APP_R2_PUBLIC_URL.'
          )
        );
        setListState('error');
      } else {
        setListState('ready');
      }
    } catch {
      setItems([]);
      setError(t('tutorials.errorList', 'Ne mogu da učitam listu tutorijala.'));
      setListState('error');
    }
  };

  const clearPlayer = () => {
    try {
      videoRef.current?.pause?.();
    } catch {
      // ignore
    }
    setSelected(null);
  };

  useEffect(() => {
    handleLoadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only manifest fetch
  }, []);

  useEffect(() => {
    if (!selected?.videoUrl) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selected?.videoUrl]);

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-green-700">
          <div className="flex items-center gap-4">
            <div className="bg-green-500/20 rounded-lg p-4">
              <BookOpen className="h-8 w-8 text-green-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('tutorials.videoSectionTitle', 'Video tutorijali')}
              </h1>
              <p className="text-gray-400 mt-1 text-sm leading-relaxed">
                {t(
                  'tutorials.videoSectionSubtitle',
                  'Lista se učitava odmah. Kliknite Play pored naslova da vidite opis i automatski pokrenete video.'
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          {listState === 'loading' && (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('tutorials.loadingList', 'Učitavanje liste...')}
            </div>
          )}

          {listState === 'error' && error && (
            <div className="space-y-3">
              <p className="text-amber-200/90 text-sm">{error}</p>
              <button
                type="button"
                onClick={handleLoadList}
                className="inline-flex items-center justify-center rounded-lg border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
              >
                {t('tutorials.retryLoadList', 'Pokušaj ponovo')}
              </button>
            </div>
          )}

          {listState === 'ready' && items.length > 0 && (
            <ul className="divide-y divide-gray-700 border border-gray-700 rounded-lg overflow-hidden">
              {items.map((item, idx) => {
                const isActive = selected?.id === item.id;
                return (
                  <li
                    key={item.id || idx}
                    className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
                      isActive ? 'bg-green-900/25' : 'bg-gray-900/40'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-white">
                        {idx + 1}. {item.title || `Tutorijal ${idx + 1}`}
                      </h3>
                      {item.duration ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t('tutorials.durationLabel', 'Trajanje')}: {item.duration}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        trackTutorialEvent({
                          event: 'play',
                          source: 'dashboard_manifest',
                          tutorialId: item.id || `idx-${idx}`,
                          title: item.title || ''
                        });
                        setSelected(item);
                      }}
                      className="inline-flex items-center justify-center gap-2 shrink-0 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                      aria-label={`Play: ${item.title || item.id}`}
                    >
                      <Play className="h-4 w-4" />
                      Play
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </div>

      {selected?.videoUrl && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-player-dash-title"
          onClick={clearPlayer}
        >
          <div
            className="relative w-full max-w-5xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-green-700 bg-gray-900 shadow-2xl p-4 sm:p-6 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={clearPlayer}
              className="absolute right-3 top-3 z-10 rounded-full p-2 text-gray-400 hover:bg-gray-800 hover:text-white"
              aria-label={t('tutorials.closePlayer', 'Zatvori plejer')}
            >
              <X className="h-5 w-5" />
            </button>
            <h2
              id="tutorial-player-dash-title"
              className="text-lg sm:text-xl font-semibold text-white pr-12"
            >
              {(() => {
                const i = items.findIndex((t) => (t.id || '') === (selected.id || ''));
                const prefix = i >= 0 ? `${i + 1}. ` : '';
                return `${prefix}${selected.title || selected.id}`;
              })()}
            </h2>
            {selected.shortDescription ? (
              <p className="text-sm text-gray-400 mt-2 mb-3 leading-relaxed">{selected.shortDescription}</p>
            ) : null}
            <video
              ref={videoRef}
              key={selected.videoUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="w-full rounded-xl bg-black max-h-[min(72dvh,640px)] object-contain"
              src={selected.videoUrl}
            >
              {t('tutorials.videoUnsupported', 'Vaš pregledač ne podržava video.')}
            </video>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialsView;
